/** The parts of the session `POST session/activate` returns that decide the redirect. */
export interface ActivatedSession {
  isOrganizationAdmin?: boolean;
  organization?: { id?: unknown } | null;
}

/**
 * Where a newly invited admin goes once they have set their password.
 *
 * Accepting an invite is always a first login, since the invite key is single
 * use. An organisation admin is sent into the organisation settings wizard in
 * onboarding mode so they set up branding, compliance and Stripe before
 * fundraising. Anyone else, such as a campaign or event admin, lands on the
 * dashboard as before.
 *
 * Reads the activation session rather than the account update response: the
 * account model only lists memberships, while the session names the active
 * organisation. `isOrganizationAdmin` is true only for an organisation context,
 * so a campaign admin whose session still names an organisation is not caught.
 *
 * The id never comes from the query string, and only a positive integer is
 * accepted, so the target is always a fixed internal route.
 */
export const getPostActivationPath = (
  session: ActivatedSession | null | undefined
): string => {
  if (!session?.isOrganizationAdmin) {
    return '/';
  }
  const organizationId = session.organization?.id;
  const id =
    typeof organizationId === 'number' ||
    (typeof organizationId === 'string' && /^\d+$/.test(organizationId))
      ? Number(organizationId)
      : NaN;
  if (!Number.isSafeInteger(id) || id <= 0) {
    return '/';
  }
  return `/organizations/edit/${id}/about?onboarding=1`;
};
